import type {LayoutTemplate} from "@o2d/grid-layout";

/**
 * The column structure of a timeline view, read out of its layout template.
 *
 * @property fixedSectionIds - Ids of the fixed row-header columns, in template order.
 * @property fixedValues     - One identity counter per fixed grid COLUMN (not per section).
 * @property fluidSectionId  - Id of the single fluid column that carries the dates.
 */
export interface XAxisSections {
    fixedSectionIds: string[];
    fixedValues: number[];
    fluidSectionId: string;
}

/**
 * Reads the horizontal structure of a timeline out of its layout template.
 *
 * This is the ONE place that turns "which columns does this view have" into the shapes the
 * rest of the timeline needs. Two consumers share it, and that sharing is the point:
 *
 * - `createTimelineAxisConfigurations` builds axis A (the fixed row-header columns) from
 *   `fixedSectionIds` and its generator from `fixedValues`, and axis B (the dates) from
 *   `fluidSectionId`.
 * - `createTimelineDataProviders` spans the row-header item across `fixedValues`.
 *
 * Before this existed, the same fact — "the row header has three fixed columns" — was
 * spelled out three times by hand, in two different files, with nothing tying them
 * together. Adding a fourth column meant editing all three in lockstep; missing one gave a
 * runtime crash far from the edit.
 *
 * Order is taken straight from `axisX` and must stay that way: the grid resolves a
 * section's position via `layoutTemplate.axisX.findIndex(...)`, so the array order IS the
 * on-screen left-to-right order.
 *
 * @param template - The layout template of the view (t3 for most views, t4 for the board).
 * @returns The fixed column ids, one axis value per fixed grid column, and the fluid id.
 * @throws If the template does not have exactly one fluid column, or has no fixed column.
 */
export const deriveXAxisSections = (template: LayoutTemplate): XAxisSections => {
    const fluidSections = template.axisX.filter(section => section.type === 'fluid');

    // Guarded rather than assumed: `find` on an empty result would hand `undefined` down as
    // a sectionId, and the failure would surface much later as the grid's own
    // "Grid section with id undefined not found".
    if (fluidSections.length !== 1) {
        throw new Error(
            `Layout template "${template.name}" must declare exactly one fluid column on axisX, found ${fluidSections.length}.`
        );
    }

    const fixedSections = template.axisX.filter(section => section.type === 'fix');

    if (fixedSections.length === 0) {
        throw new Error(
            `Layout template "${template.name}" must declare at least one fixed column on axisX.`
        );
    }

    // Sum `count`, do not count sections: a section renders as `repeat(count, ...)` grid
    // tracks, so one section with count 2 occupies two columns and needs two axis values.
    // Every section is count 1 today, which is exactly why this is easy to get wrong later.
    const columnCount = fixedSections.reduce(
        (total, section) => total + (typeof section.count === 'number' ? section.count : 0),
        0
    );

    return {
        fixedSectionIds: fixedSections.map(section => section.sectionId),
        // Identity counters: axis A's values carry no meaning, only their number matters —
        // it is what gives the axis one slot per fixed grid column.
        fixedValues: Array.from({length: columnCount}, (_, index) => index),
        fluidSectionId: fluidSections[0].sectionId
    };
};

/**
 * template für die meisten timeline-views
 * Standard layout template for all timeline views.
 * Defines 4 column sections (3 fixed + 1 fluid) and 6 row sections (5 fixed + 1 fluid).
 * row-section-calendar is a fixed 50px row between the date header (row-section-3)
 * and the scrollable entity rows (row-section-4).
 */
export const TIMELINE_LAYOUT_TEMPLATE: LayoutTemplate = {
    name: 't3',
    axisX: [
        {sectionId: 'column-section-0', count: 1, type: 'fix', sizeCssVariable: 'column-section-0-size'},
        {sectionId: 'column-section-1', count: 1, type: 'fix', sizeCssVariable: 'column-section-1-size'},
        {sectionId: 'column-section-2', count: 1, type: 'fix', sizeCssVariable: 'column-section-2-size'},
        {sectionId: 'column-section-3', count: 'auto', type: 'fluid', sizeCssVariable: 'column-section-3-size'}
    ],
    axisY: [
        {sectionId: 'row-section-0', count: 1, type: 'fix', sizeCssVariable: 'row-section-0-size'},
        {sectionId: 'row-section-1', count: 1, type: 'fix', sizeCssVariable: 'row-section-1-size'},
        {sectionId: 'row-section-2', count: 1, type: 'fix', sizeCssVariable: 'row-section-2-size'},
        {sectionId: 'row-section-3', count: 1, type: 'fix', sizeCssVariable: 'row-section-3-size'},
        {sectionId: 'row-section-calendar', count: 1, type: 'fix', sizeCssVariable: 'row-section-calendar-size'},
        {sectionId: 'row-section-space', count: 1, type: 'fix', sizeCssVariable: 'row-section-space-size'},
        {sectionId: 'row-section-4', count: 'auto', type: 'fluid', sizeCssVariable: 'row-section-4-size'}
    ]
};

/**
 * Standard CSS variables for timeline section sizes.
 * Used by all timeline views via getSectionSizeVariables().
 *
 * Note (PRD-060): `--column-section-3-size` is intentionally omitted.
 * The date-column width is now defined exclusively in the designer CSS
 * (`backend/public/assets/css/spa.css` as `--t3-column-section-3-size`)
 * via `calc(40px * var(--cell-width-multiplication, 1))`. The reactive
 * bridge `initCellWidthMultiplicationBridge()` in `abstract-o2d-timeline.view.ts`
 * updates the multiplier; the base width stays in CSS (CSS-Location-Law).
 */
export const TIMELINE_SECTION_SIZES: Record<string, string> = {
    '--column-section-0-size': '200px',
    '--column-section-1-size': '40px',
    '--column-section-2-size': '40px',
    '--column-section-4-size': '40px',
    '--row-section-0-size': '30px',
    '--row-section-1-size': '25px',
    '--row-section-2-size': '20px',
    '--row-section-3-size': '25px',
    '--row-section-calendar-size': '50px',
    '--row-section-4-size': '50px',
};

/**
 * template für die board-view (inkl. spalte für add milestone)
 */
export const TIMELINE_LAYOUT_TEMPLATE_T4: LayoutTemplate = {
    name: 't4',
    axisX: [
        {sectionId: 'column-section-0', count: 1, type: 'fix', sizeCssVariable: 'column-section-0-size'},
        {sectionId: 'column-section-1', count: 1, type: 'fix', sizeCssVariable: 'column-section-1-size'},
        {sectionId: 'column-section-4', count: 1, type: 'fix', sizeCssVariable: 'column-section-4-size'},
        {sectionId: 'column-section-2', count: 1, type: 'fix', sizeCssVariable: 'column-section-2-size'},
        {sectionId: 'column-section-3', count: 'auto', type: 'fluid', sizeCssVariable: 'column-section-3-size'}
    ],
    axisY: [
        {sectionId: 'row-section-0', count: 1, type: 'fix', sizeCssVariable: 'row-section-0-size'},
        {sectionId: 'row-section-1', count: 1, type: 'fix', sizeCssVariable: 'row-section-1-size'},
        {sectionId: 'row-section-2', count: 1, type: 'fix', sizeCssVariable: 'row-section-2-size'},
        {sectionId: 'row-section-3', count: 1, type: 'fix', sizeCssVariable: 'row-section-3-size'},
        {sectionId: 'row-section-calendar', count: 1, type: 'fix', sizeCssVariable: 'row-section-calendar-size'},
        {sectionId: 'row-section-space', count: 1, type: 'fix', sizeCssVariable: 'row-section-space-size'},
        {sectionId: 'row-section-4', count: 'auto', type: 'fluid', sizeCssVariable: 'row-section-4-size'}
    ]
};

